"use client";

import { useState, type MouseEvent } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ArrowRight, PlayCircle } from "lucide-react";

import { useAuth } from "@/components/providers/auth-provider";
import { authRefresh } from "@/features/auth/api";

import { formatDateTime } from "../lib/format";
import { useContestDisplayZone } from "../lib/display-zone";
import type { PublicContestDetail } from "../types";

/**
 * The warm-up's line on the contest page's door (`EX-DAY04`, `docs/29` §6.10).
 *
 * On a warm-up: which contest it warms up for, and that it is never rated. On
 * the main contest: the warm-up, for the people who can enter it or run it.
 *
 * An issued login's routing hint is set at sign-in and at each refresh, so a
 * warm-up scheduled after this sign-in is not in it yet, and the middleware
 * would send the click straight back here. That login refreshes its session
 * first, which rewrites the hint, and then opens the warm-up.
 */
export function WarmUpDoorNote({ contest, locale }: { contest: PublicContestDetail; locale: string }) {
  const t = useTranslations("contest.page.warmUp");
  const zone = useContestDisplayZone();
  const { user } = useAuth();
  const [opening, setOpening] = useState(false);

  if (contest.registration === "linked") {
    const main = contest.warmUpOf ?? null;
    return (
      <div className="mb-4 flex items-start gap-2 rounded-[10px] border border-white/10 bg-white/5 px-4 py-3 text-sm text-neutral-200" data-testid="warm-up-of-note">
        <PlayCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent-peach" aria-hidden />
        <p>
          {main ? (
            t.rich("forMain", {
              link: (chunks) => (
                <Link href={`/${locale}/contests/${main.slug}`} className="font-semibold text-accent-peach underline underline-offset-2">
                  {chunks}
                </Link>
              ),
              title: locale === "bn" ? (main.title_bn ?? main.title_en) : main.title_en,
            })
          ) : (
            t("forMainUnnamed")
          )}{" "}
          {t("neverRated")}
        </p>
      </div>
    );
  }

  const warmUp = contest.viewer.warmUp;
  if (!warmUp) return null;
  const href = `/${locale}/contests/${warmUp.slug}`;
  const staleHint =
    user?.accountKind === "contest_temp" && user.tempContest?.warmUp?.slug !== warmUp.slug;
  const open = async (event: MouseEvent<HTMLAnchorElement>) => {
    if (!staleHint) return;
    event.preventDefault();
    setOpening(true);
    try {
      await authRefresh();
    } finally {
      window.location.assign(href);
    }
  };
  return (
    <div className="mb-4 rounded-[10px] border border-white/10 bg-white/5 px-4 py-3 text-sm text-neutral-200" data-testid="warm-up-note">
      <p className="flex items-center gap-2 font-semibold text-white">
        <PlayCircle className="h-4 w-4 shrink-0 text-accent-peach" aria-hidden />
        {t("title")}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-neutral-400">
        {t("window", { start: formatDateTime(warmUp.startAt, locale, zone), end: formatDateTime(warmUp.endAt, locale, zone) })}
      </p>
      <Link
        href={href}
        onClick={(event) => void open(event)}
        aria-disabled={opening}
        data-testid="warm-up-link"
        className="mt-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-accent-peach transition-colors duration-200 hover:text-white"
      >
        {warmUp.entered ? t("open") : t("enter")}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </div>
  );
}
